import { HierarchicalTableConfig } from '../HierarchicalTable';
import { TableRow, isExploitGroupRow } from './post-optimization-constants';

/**
 * Shared configuration for PostOptimization's hierarchical table
 *
 * This config is used by:
 * - Filter factories (createMultiSelectFilter, createNumberRangeFilter)
 * - Table setup (getHierarchicalTableDefaults)
 * - Row styling (getHierarchicalRowClassName)
 *
 * Uses TableRow (not TopLevelRow) because the table displays all row types
 * when groups are expanded.
 */
export const postOptimizationConfig: HierarchicalTableConfig<TableRow> = {
  // Row type detection
  getRowType: row => (row.type === 'lineItem' ? 'leaf' : row.type),

  // Group-level values for leaf filter inheritance. Explicit cases (rather than
  // an index lookup) so renaming a GroupLevelValues field fails to compile here.
  // Non-group-level columns must return undefined.
  getInheritedValue: (row, columnId) => {
    if (row.type !== 'lineItem') {
      return undefined;
    }
    const { parentValues } = row;
    switch (columnId) {
      case 'networkContent':
        return parentValues.networkContent;
      case 'format':
        return parentValues.format;
      case 'exploitExplore':
        return parentValues.exploitExplore;
      case 'budget':
        return parentValues.budget;
      case 'aggregateCpm':
        return parentValues.aggregateCpm;
      default:
        return undefined;
    }
  },

  // Table structure
  getSubRows: row => ('subRows' in row ? row.subRows : undefined),
  getRowId: row => row.id,

  // Styling
  isEdited: row => row.isEdited ?? false,
  hasEditedChildren: row =>
    isExploitGroupRow(row) ? row.subRows.some(lineItem => lineItem.isEdited) : false
};
